import { useCallback, useState } from 'react'

import { IconCog, TextButton, Tooltip } from '@swaggerhub-portal/component-library'
import { useTranslation } from 'react-i18next'
import { useHydrated } from 'remix-utils/use-hydrated'

import { getRoutingType } from '~/components/ApiViewer/RoutingSettingsButton/helpers'
import { RoutingSettingsModal } from '~/components/ApiViewer/RoutingSettingsButton/RoutingSettingsModal'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { RoutingOption, RoutingSettings } from '~/types/api'

export const RoutingSettingsButton = ({
  accentColor,
  routingSettings,
}: {
  accentColor?: string,
  routingSettings?: RoutingSettings,
}) => {
  const { t } = useTranslation()

  // button label contains value that comes from local storage so we need to render
  // it after hydration in order to prevent hydration error
  const isHydrated = useHydrated()

  const defaultSettings = routingSettings || {
    routing: RoutingOption.BROWSER,
    credentialsEnabled: true,
  }
  const [{ routing, credentialsEnabled }, setRoutingSettings] = useLocalStorage<RoutingSettings>('portal.proxyOptions', defaultSettings)

  const [open, setOpen] = useState(false)
  const [routingType, setRoutingType] = useState(getRoutingType(routing, credentialsEnabled))

  const toggleModal = () => setOpen(!open)

  const onModalConfirm = useCallback((routing: RoutingOption, credentialsEnabled: boolean) => {
    setRoutingType(getRoutingType(routing, credentialsEnabled))
    setRoutingSettings({
      routing,
      credentialsEnabled,
    })
    setOpen(false)
  }, [])

  return (
    <>
      <Tooltip
        content={t('components.ui.apiViewer.routing.button.tooltip')}
        trigger="hover"
        defaultComp={
          <TextButton
            data-testid="routing-button"
            size="s"
            icon={<IconCog width={12} height={12} />}
            className="ml-0 duration-200 transition-color hover:bg-neutral-80 hover:text-neutral-00 dark:hover:bg-neutral-30 dark:hover:text-neutral-98"
            onClick={toggleModal}
            loading={!isHydrated}
          >
            {t('components.ui.apiViewer.routing.button.label')}
            {isHydrated ? (
              <>: {t(`components.ui.apiViewer.routing.button.types.${routingType}`)}</>
            ) : null}
          </TextButton>
        }
        placement="right"
      />
      {open ? (
        <RoutingSettingsModal
          isOpen={open}
          initialRouting={routing}
          initialCredentialsEnabled={credentialsEnabled}
          onConfirm={onModalConfirm}
          onClose={toggleModal}
          accentColor={accentColor}
        />
      ) : null}
    </>
  )
}